import type { PickerChangeHandlerContext } from '@mui/x-date-pickers-pro';
import { format, isValid } from 'date-fns';
import type { KeyboardEvent } from 'react';
import { API_DATE_FORMAT, parseApiDate } from '../../../utils/dateTime';
import { DEFERRED_DATE_EDITING_KEYS } from '../../formConstants';

export type CommittedDateValue = string | Date | null | undefined;
export type DeferredDateValidationError = PickerChangeHandlerContext<string>['validationError'];

export const verifyIsEmptyCommittedDateValue = (value: CommittedDateValue) =>
    value === '' || value === null || typeof value === 'undefined';

export const compareCommittedDateValues = (left: CommittedDateValue, right: CommittedDateValue) => {
    if (verifyIsEmptyCommittedDateValue(left) && verifyIsEmptyCommittedDateValue(right)) {
        return true;
    }

    return left === right;
};

export const verifyIsDeferredDateEditingKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) {
        return false;
    }

    return event.key.length === 1 || DEFERRED_DATE_EDITING_KEYS.includes(event.key);
};

export const constructCommittedDateValue = (
    newValue: Date | null,
    validationError: DeferredDateValidationError,
    currentValue: CommittedDateValue
): string | Date | null => {
    const isValidDate = isValid(newValue);

    return newValue && isValidDate
        ? format(newValue, API_DATE_FORMAT)
        : validationError
          ? (currentValue ?? null)
          : newValue;
};

type ConstructPickerDateValueParams = {
    deferKeyboardDateCommitUntilBlur: boolean;
    hasDeferredDateValue: boolean;
    deferredDateValue: Date | null;
    value: CommittedDateValue;
};

export const constructPickerDateValue = ({
    deferKeyboardDateCommitUntilBlur,
    hasDeferredDateValue,
    deferredDateValue,
    value
}: ConstructPickerDateValueParams) => {
    if (deferKeyboardDateCommitUntilBlur && hasDeferredDateValue) {
        return deferredDateValue;
    }

    if (value instanceof Date) {
        return value;
    }

    return value ? (parseApiDate(value) ?? null) : null;
};
